import React, { useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import { Button, Card, Col, Dropdown, Row, Select, Table, Tabs } from "antd";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import ExcelExport from "components/shared-components/ExcelExport";
import { COMPANY_CONSTANTS } from "constants/CompanyConstants";
import { DATE_FORMAT_DD_MM_YYYY } from "constants/DateConstant";
import { DownOutlined, FileExcelOutlined } from "@ant-design/icons";
import { PeriodSelector } from "../products/components";
import { getOrdersInPeriod } from "functions/firestore/orders";
import { ORDER_STATUS_CREATED } from "constants/OrderConstant";
import { formatPrice } from "functions/formatters";
import { CURRENCY } from "constants/DeliveryConstants";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Line,
  LineChart,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

const COLORS = ["#8884d8", "#82ca9d", "#ffc658", "#ff7f50", "#8dd1e1", "#a4de6c", "#d0ed57", "#ffc0cb"];

const SellersReport = () => {
  const { t } = useTranslation();
  const users = useSelector((state) => state.firestoreReducer.ordered.salesPersons);

  const [period, setPeriod] = useState("month");
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [ordersYear, setOrdersYear] = useState([]);
  const [ordersInPeriod, setOrdersInPeriod] = useState([]);
  const [activeTab, setActiveTab] = useState("overview");
  const [selectedSellerIds, setSelectedSellerIds] = useState([]);

  // Initial fetch (past year)
  useEffect(() => {
    const fetchYear = async () => {
      try {
        setLoading(true);
        const yearStartDate = dayjs().subtract(1, "year").startOf("day").unix();
        const yearEndDate = dayjs().subtract(1, "day").endOf("day").unix();
        const fetched = await getOrdersInPeriod(yearStartDate, yearEndDate);
        setOrdersYear(fetched || []);
        setOrdersInPeriod(fetched || []);
      } catch (e) {
        console.error("Error fetching orders for sellers report", e);
      } finally {
        setLoading(false);
      }
    };
    fetchYear();
  }, []);

  // Update period filter locally
  useEffect(() => {
    if (!ordersYear.length) return;
    const startUnix = !!startDate ? dayjs(startDate).unix() : getStartDateForPeriod(period);
    const endUnix = !!endDate ? dayjs(endDate).unix() : dayjs().subtract(1, "day").endOf("day").unix();

    const filtered = ordersYear.filter((o) => o.createdAt >= startUnix && o.createdAt <= endUnix);
    setOrdersInPeriod(filtered);
  }, [period, startDate, endDate, ordersYear]);

  // Determine active sellers for current period and set default selection
  useEffect(() => {
    const active = Array.from(
      new Set(
        (ordersInPeriod || [])
          .filter((o) => o.orderStatus !== ORDER_STATUS_CREATED && !!o.salesPersonId)
          .map((o) => o.salesPersonId),
      ),
    );
    setSelectedSellerIds(active);
  }, [ordersInPeriod]);

  // Aggregations per seller
  const { sellerSummary, displayedSellerSummary, revenueBySeller, pieData, trendData, topSellers } = useMemo(() => {
    const summaryMap = new Map();

    // Only orders with a salesperson assigned and not in CREATED status
    const relevantOrders = (ordersInPeriod || []).filter(
      (o) => o.orderStatus !== ORDER_STATUS_CREATED && !!o.salesPersonId,
    );

    relevantOrders.forEach((order) => {
      const key = order.salesPersonId;
      if (!summaryMap.has(key)) {
        summaryMap.set(key, {
          sellerId: order.salesPersonId,
          orders: 0,
          items: 0,
          revenue: 0,
          revenueBySeller: 0,
          revenueByCustomer: 0,
          productsMap: new Map(),
        });
      }
      const entry = summaryMap.get(key);
      entry.orders += 1;
      const orderRevenue = (order.products || []).reduce(
        (sum, p) => sum + (p.paymentPrice || 0) * (p.quantity || 0),
        0,
      );
      if (order.orderPlacedBySalesPerson) {
        entry.revenueBySeller += orderRevenue;
      } else {
        entry.revenueByCustomer += orderRevenue;
      }
      (order.products || []).forEach((p) => {
        entry.items += p.quantity || 0;
        entry.revenue += (p.paymentPrice || 0) * (p.quantity || 0);
        const prodKey = p.productId || p.name;
        if (!entry.productsMap.has(prodKey)) {
          entry.productsMap.set(prodKey, {
            productId: p.productId,
            name: p.name,
            quantity: 0,
            revenue: 0,
          });
        }
        const prod = entry.productsMap.get(prodKey);
        prod.quantity += p.quantity || 0;
        prod.revenue += (p.paymentPrice || 0) * (p.quantity || 0);
      });
    });

    // Helper: find salesperson and name/commission
    const getSalesPersonById = (id) => (users || []).find((u) => u.id === id);

    // Build arrays
    const sellerSummaryArr = Array.from(summaryMap.entries()).map(([key, val]) => {
      const sp = getSalesPersonById(val.sellerId);
      const name = sp ? `${sp.firstName || ""} ${sp.lastName || ""}`.trim() : "-";
      const commissionPercent =
        sp && sp.salesPerson && typeof sp.salesPerson.commission === "number" ? sp.salesPerson.commission : 10;
      const productsArr = Array.from(val.productsMap.values()).sort((a, b) => b.quantity - a.quantity);
      return {
        sellerKey: key,
        sellerId: val.sellerId,
        sellerName: name,
        orders: val.orders,
        items: val.items,
        revenue: val.revenue,
        revenueBySeller: val.revenueBySeller,
        revenueByCustomer: val.revenueByCustomer,
        avgOrderValue: val.orders > 0 ? val.revenue / val.orders : 0,
        commissionPercent,
        commissionAmount: (val.revenue * commissionPercent) / 100,
        topProducts: productsArr.slice(0, 10),
      };
    });

    // Apply current selection (default = all active)
    const selectedSet = new Set(selectedSellerIds);
    const filteredSummary = sellerSummaryArr.filter((s) => selectedSet.has(s.sellerId));

    const revenueBySellerArr = filteredSummary
      .map((s) => ({
        name: s.sellerName,
        revenue: Number(s.revenue.toFixed(2)),
        revenueBySeller: Number(s.revenueBySeller.toFixed(2)),
        revenueByCustomer: Number(s.revenueByCustomer.toFixed(2)),
      }))
      .sort((a, b) => b.revenue - a.revenue);

    const pieDataArr = filteredSummary.map((s) => ({ name: s.sellerName, value: Number(s.revenue.toFixed(2)) }));

    // Trend by date per seller (top 5 sellers to keep the chart readable)
    const top = [...revenueBySellerArr].slice(0, 5).map((s) => s.name);

    const dateBuckets = new Map();
    (ordersInPeriod || [])
      .filter(
        (o) =>
          o.orderStatus !== ORDER_STATUS_CREATED &&
          !!o.salesPersonId &&
          new Set(selectedSellerIds).has(o.salesPersonId),
      )
      .forEach((order) => {
        const dateStr = dayjs.unix(order.createdAt).format(DATE_FORMAT_DD_MM_YYYY);
        if (!dateBuckets.has(dateStr)) dateBuckets.set(dateStr, {});
        const bucket = dateBuckets.get(dateStr);
        const sp = (users || []).find((u) => u.id === order.salesPersonId);
        const sellerName = sp ? `${sp.firstName || ""} ${sp.lastName || ""}`.trim() : "-";
        const revenueOrder = (order.products || []).reduce(
          (sum, p) => sum + (p.paymentPrice || 0) * (p.quantity || 0),
          0,
        );
        if (!bucket[sellerName]) bucket[sellerName] = 0;
        bucket[sellerName] += revenueOrder;
      });

    const trend = Array.from(dateBuckets.entries())
      .sort((a, b) => dayjs(a[0], DATE_FORMAT_DD_MM_YYYY).unix() - dayjs(b[0], DATE_FORMAT_DD_MM_YYYY).unix())
      .map(([date, obj]) => {
        const row = { date };
        top.forEach((sellerName) => {
          row[sellerName] = Number((obj[sellerName] || 0).toFixed(2));
        });
        return row;
      });

    return {
      sellerSummary: sellerSummaryArr,
      displayedSellerSummary: filteredSummary,
      revenueBySeller: revenueBySellerArr,
      pieData: pieDataArr,
      trendData: trend,
      topSellers: top,
    };
  }, [ordersInPeriod, users, t, selectedSellerIds]);

  const getStartDateForPeriod = (selectedPeriod) => {
    const today = dayjs();
    switch (selectedPeriod) {
      case "week":
        return today.subtract(7, "day").unix();
      case "month":
        return today.subtract(30, "day").unix();
      case "quarter":
        return today.subtract(90, "day").unix();
      case "halfyear":
        return today.subtract(180, "day").unix();
      case "year":
      default:
        return today.subtract(365, "day").unix();
    }
  };

  const getFormattedStartAndEndDates = () => {
    const currentStartDate = !!startDate ? dayjs(startDate) : dayjs.unix(getStartDateForPeriod(period));
    const currentEndDate = !!endDate ? dayjs(endDate) : dayjs().subtract(1, "day").endOf("day");
    const formattedStartDate = currentStartDate.format(DATE_FORMAT_DD_MM_YYYY);
    const formattedEndDate = currentEndDate.format(DATE_FORMAT_DD_MM_YYYY);
    return formattedStartDate + " - " + formattedEndDate;
  };

  const exportItems = [
    {
      key: "1",
      label: (
        <ExcelExport
          data={[
            {
              sheetName: t("sellers"),
              columns: [
                { title: t("sales_person"), dataIndex: "sellerName" },
                { title: t("orders"), dataIndex: "orders" },
                { title: t("items"), dataIndex: "items" },
                { title: t("revenue"), dataIndex: "revenue" },
                { title: t("placed_by_seller"), dataIndex: "revenueBySeller" },
                { title: t("placed_by_customer"), dataIndex: "revenueByCustomer" },
                { title: t("average_order_value"), dataIndex: "avgOrderValue" },
                { title: t("commission"), dataIndex: "commissionPercent" },
                { title: t("commission_amount"), dataIndex: "commissionAmount" },
              ],
              data: (displayedSellerSummary || []).map((s) => [
                { value: s.sellerName },
                { value: s.orders },
                { value: s.items },
                { value: CURRENCY + formatPrice(s.revenue) },
                { value: CURRENCY + formatPrice(s.revenueBySeller) },
                { value: CURRENCY + formatPrice(s.revenueByCustomer) },
                { value: CURRENCY + formatPrice(s.avgOrderValue) },
                { value: `${s.commissionPercent}%` },
                { value: CURRENCY + formatPrice(s.commissionAmount) },
              ]),
            },
          ]}
          fileName={COMPANY_CONSTANTS.WEBSITE_NAME + " verkopers " + getFormattedStartAndEndDates()}
        >
          {t("excel")}
        </ExcelExport>
      ),
    },
  ];

  const columns = [
    {
      title: t("sales_person"),
      dataIndex: "sellerName",
    },
    {
      title: t("orders"),
      dataIndex: "orders",
      align: "right",
      sorter: (a, b) => a.orders - b.orders,
    },
    {
      title: t("items"),
      dataIndex: "items",
      align: "right",
      sorter: (a, b) => a.items - b.items,
    },
    {
      title: t("revenue"),
      dataIndex: "revenue",
      align: "right",
      render: (v) => <span style={{ whiteSpace: "nowrap" }}>{CURRENCY + formatPrice(v)}</span>,
      sorter: (a, b) => a.revenue - b.revenue,
    },
    {
      title: t("placed_by_seller"),
      dataIndex: "revenueBySeller",
      align: "right",
      render: (v) => <span style={{ whiteSpace: "nowrap" }}>{CURRENCY + formatPrice(v)}</span>,
      sorter: (a, b) => a.revenueBySeller - b.revenueBySeller,
    },
    {
      title: t("placed_by_customer"),
      dataIndex: "revenueByCustomer",
      align: "right",
      render: (v) => <span style={{ whiteSpace: "nowrap" }}>{CURRENCY + formatPrice(v)}</span>,
      sorter: (a, b) => a.revenueByCustomer - b.revenueByCustomer,
    },
    {
      title: t("average_order_value"),
      dataIndex: "avgOrderValue",
      align: "right",
      render: (v) => <span style={{ whiteSpace: "nowrap" }}>{CURRENCY + formatPrice(v)}</span>,
      sorter: (a, b) => a.avgOrderValue - b.avgOrderValue,
    },
    {
      title: t("commission"),
      dataIndex: "commissionPercent",
      align: "right",
      render: (v, r) => <span style={{ whiteSpace: "nowrap" }}>{`${r.commissionPercent}%`}</span>,
      sorter: (a, b) => a.commissionPercent - b.commissionPercent,
    },
    {
      title: t("commission_amount"),
      dataIndex: "commissionAmount",
      align: "right",
      render: (v) => <span style={{ whiteSpace: "nowrap" }}>{CURRENCY + formatPrice(v)}</span>,
      sorter: (a, b) => a.commissionAmount - b.commissionAmount,
    },
  ];

  return (
    <DashboardContainer>
      <Title>{t("sellers_report")}</Title>
      <Card>
        {/*<FlexContainer>*/}
        <Row gutter={16}>
          <Col xs={24} lg={10}>
            <PeriodSelector
              period={period}
              setPeriod={setPeriod}
              startDate={startDate}
              setStartDate={setStartDate}
              endDate={endDate}
              setEndDate={setEndDate}
            />
          </Col>
          <Col xs={24} lg={10}>
            <div>
              <Label>{t("period")}</Label>
              <Select
                mode="multiple"
                style={{ width: "100%" }}
                placeholder={t("sellers")}
                value={selectedSellerIds}
                onChange={setSelectedSellerIds}
                options={(users || []).map((u) => ({
                  value: u.id,
                  label: `${u.firstName || ""} ${u.lastName || ""}`.trim(),
                }))}
              />
            </div>
          </Col>
          <Col xs={24} lg={4}>
            <Dropdown menu={{ items: exportItems }}>
              <Button className="ant-btn-primary">
                <FileExcelOutlined /> {t("export")} <DownOutlined />
              </Button>
            </Dropdown>
          </Col>
        </Row>
        {/*</FlexContainer>*/}
      </Card>

      <Tabs
        activeKey={activeTab}
        onChange={setActiveTab}
        items={[
          { key: "overview", label: t("overview") },
          { key: "trends", label: t("trends") },
        ]}
      />

      {activeTab === "overview" && (
        <>
          <CardSection>
            <SectionTitle>{t("revenue")}</SectionTitle>
            <ChartContainer>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={revenueBySeller} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" hide={false} />
                  <YAxis tickFormatter={(v) => CURRENCY + formatPrice(v)} />
                  <Tooltip
                    formatter={(value, name) => [
                      CURRENCY + formatPrice(value),
                      name === "revenueBySeller" ? t("placed_by_seller") : t("placed_by_customer"),
                    ]}
                  />
                  <Legend
                    formatter={(value) =>
                      value === "revenueBySeller" ? t("placed_by_seller") : t("placed_by_customer")
                    }
                  />
                  <Bar dataKey="revenueBySeller" stackId="a" fill="#8884d8" name="revenueBySeller" />
                  <Bar dataKey="revenueByCustomer" stackId="a" fill="#82ca9d" name="revenueByCustomer" />
                </BarChart>
              </ResponsiveContainer>
            </ChartContainer>
          </CardSection>

          <CardSection>
            <SectionTitle>{t("percentage_of_total")}</SectionTitle>
            <ChartContainer>
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    dataKey="value"
                    data={pieData}
                    label={(entry) => `${entry.name}: ${CURRENCY + formatPrice(entry.value)}`}
                    outerRadius={120}
                  >
                    {pieData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(value) => [CURRENCY + formatPrice(value), t("revenue")]} />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </ChartContainer>
          </CardSection>

          <Card>
            <SectionTitle>{t("details")}</SectionTitle>
            <Table
              loading={loading}
              dataSource={displayedSellerSummary}
              columns={columns}
              rowKey={(r) => r.sellerKey}
              pagination={{ pageSize: 10 }}
              scroll={{ x: 750 }}
            />
          </Card>
        </>
      )}

      {activeTab === "trends" && (
        <CardSection>
          <SectionTitle>{t("weekly_revenue")}</SectionTitle>
          <ChartContainer>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={trendData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" />
                <YAxis tickFormatter={(v) => CURRENCY + formatPrice(v)} />
                <Tooltip formatter={(value) => [CURRENCY + formatPrice(value), t("revenue")]} />
                <Legend />
                {topSellers.map((name, idx) => (
                  <Line key={name} type="monotone" dataKey={name} stroke={COLORS[idx % COLORS.length]} />
                ))}
              </LineChart>
            </ResponsiveContainer>
          </ChartContainer>
        </CardSection>
      )}
    </DashboardContainer>
  );
};

export default SellersReport;

// Styles
const Label = styled.label`
  display: block;
  font-size: 0.875rem;
  font-weight: 500;
  color: #4b5563;
  margin-bottom: 0.25rem;
`;

const DashboardContainer = styled.div`
  padding: 1rem;
  background-color: #f9fafb;
  min-height: 100vh;
`;

const Title = styled.h1`
  font-size: 1.5rem;
  font-weight: 600;
  margin-bottom: 1rem;
`;

const FlexContainer = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 12px;
`;

const SectionTitle = styled.h2`
  font-size: 1.25rem;
  font-weight: 600;
  margin-bottom: 1rem;
`;

const CardSection = styled(Card)`
  margin-top: 16px;
`;

const ChartContainer = styled.div`
  height: 24rem;
`;
